/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useEffect } from "react";
import React, { useState, useMemo } from 'react';
import Pagination from "../../../../widgets/pagination";
import MediaPrepHeaderSection from "../../../common/commonComponents/mediaPrepheaderSection";
import MediaPrepPageHeader from "../../../common/commonComponents/mediaPrepPageHeader";
import { MediaPowderService } from "../../../../service/MediaPowderServices";
import { DEFAULT_PAGE_SIZE } from "../../../../constants/constants";
import FilterBar from "./partials/filterBar";
import { userService as plateRelatedService } from '../../../../service/plateServices';
import MediaPreparationTable from "../../../common/commonComponents/mediaPreparationTable";
import { getColumns } from "./partials/column";
import StatusCard from "../../../common/commonComponents/statusCard";
import { useTranslation } from 'react-i18next';
import MediaLotDetails from "./views/mediaLotDetails";
import BatchLotUsageDetails from "./views/batchLotUsageDetails";
import LogMediaPowderModal from "./partials/logMediaModal";
import PowderLotCreation from "./views/powderLotCreation";
import CustomToast from "../../../../widgets/toaster";
import { useNavigate } from "react-router-dom";
import { userService } from "../../../../service/userService";

const MediaPowderInventory: React.FC = () => {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [lotFilter, setLotFilter] = useState('');
  const [mediaTypeFilter, setMediaTypeFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [inventoryItems, setInventoryItems] = useState<any[]>([]);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [plateTypes, setPlateTypes] = useState([]);
  const [batchCodes, setBatchCodes] = useState<string[]>([]);
  const [selectedLotId, setSelectedLotId] = useState<string>('');
  const [previousLotId, setPreviousLotId] = useState<string>('');
  const [view, setView] = useState<"list" | "lot" | "batch" | "usage">("list");
  const [selectedBatchId, setSelectedBatchId] = useState<string>(""); 
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("");
  const navigate = useNavigate();
  const [permission, setPermission] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  

  const [sort, setSort] = useState("id");
  const [order, setOrder] = useState("desc");
  const mapStatusToApi = (status: string) => {
    if (status === "Expired") return "expired";
    return status;
  };

  const handleSort = (key: string, value: boolean) => {
    setSort(key);
    setOrder(value ? "asc" : "desc");
    setCurrentPage(1);
  };


  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);

    if (size) {
      setPageSize(size);
    }
  };


  const handleItemsPerPageChange = (items: number) => {
    setCurrentPage(1);
  };


  const handleClearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setStatusFilter('');
    setLotFilter('');
    setMediaTypeFilter('');
    setFromDate('');
    setToDate('');
    setCurrentPage(1);
  };

  const showToastMessage = (message: string, type: string) => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };


  const [stats, setStats] = useState({
    available: 0,
    nearExpiry: 0,
    expired: 0,
    quarantine: 0,
    blocked: 0,
  });

  const getPermissionByRole = async (role: number) => {
    try {
      const response = await userService.getPermissionByRole(role);

      if (response.status && response.statusCode === 200) {
        setPermission(response.data.permissions);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const storedData = localStorage.getItem("userDetails");

    if (storedData) {
      const storedObject = JSON.parse(storedData);
      getPermissionByRole(storedObject[0].role_master_id);
    }
  }, []);



   //api call for statistics listing for inventory
  const getMediaPowderStats = async () => {
    try {
      const response = await MediaPowderService.getMediaPowderStats();

      if (response.status && response.statusCode === 200) {
        const data = response.data;

        setStats({
          available: data.available_grams,
          nearExpiry: data.near_expiry,
          expired: data.expired,
          quarantine: data.quarantine,
          blocked: data.blocked,
        });
      }
    } catch (error) {
      console.error("Stats API error:", error);
    }
  };


    //api call for media powder data listing
  const getMediaPowderList = async () => {
    setLoading(true);
    try {
      const response = await MediaPowderService.getMediaPowderList(currentPage, pageSize, sort, order, searchTerm, statusFilter ? mapStatusToApi(statusFilter) : "", lotFilter, mediaTypeFilter, fromDate, toDate
      );

      if (response.status && response.statusCode === 200) {
        const mappedData = response.data.map((element: any, index: number) => ({
          id: element.id,
          batchCode: element.batch_name,
          name: element.media_name,
          mf: element.manufacturer_name,
          storage: element.storage_temperature,
          stock: element.available_stock,
          expiry: element.expiry_date,
          qaStatus: element.qa_release_status,
          stockStatus: element.stock_status,
          quantity: element.quantity,
          sku: element.catalog_number,
        }));

        setInventoryItems(mappedData);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
      } else {
        setInventoryItems([]);
      }
    } catch (error) {
      console.error("API error:", error);
    }
    finally {
      setLoading(false);
    }
  };



   // batch code list for filter dropdown 
  const getBatchCodes = async () => {
    try {
      const response = await MediaPowderService.getBatchCodes();

      if (response.status && response.statusCode === 200) {
        const codes = response.data.map((item: any) => item.batch_name);
        setBatchCodes(codes);
      }
    } catch (error) {
      console.error("Batch code API error:", error);
    }
  };



    //api call to get the plate types 
   const getPlateTypeList = async () => {
    try {
      const response = await plateRelatedService.listAllPlateTypes();

      if (response.status && response.statusCode === 200) {
        const formatted = response.data.map((item: any) => ({
          id: item.id,
          name: item.plate_type   
        }));

        setPlateTypes(formatted);
      }
    } catch (error) {
      console.error('user response', error);
    }
  };


  useEffect(() => {
    getMediaPowderStats();
  }, []);

  useEffect(() => {
    getMediaPowderList();
  }, [currentPage, pageSize, sort, order, searchTerm,statusFilter, lotFilter, mediaTypeFilter, fromDate, toDate]);


  useEffect(() => {
    getPlateTypeList();
  }, []);


  useEffect(() => {
    getBatchCodes();
  }, []);

  
  if (view === "usage" && selectedLotId) {
  return (
      <BatchLotUsageDetails
        lotId={selectedLotId}
        onBack={() => {
          setSelectedLotId(previousLotId); 
          setView("lot");
        }}
      />
    );
  }
  if (view === "lot" && selectedLotId) {
  return (
    <MediaLotDetails
      lotId={selectedLotId}
      selectedLotSummary={selectedRow}
      onBack={() => {
        setView("list");
        getMediaPowderList();
        getMediaPowderStats();
      }}
      onViewBatchDetails={(batchId) => {
        setSelectedBatchId(batchId);
        setView("batch");
      }}
      onViewLotUsage={(lotId) => {
        setSelectedLotId(lotId);
        setPreviousLotId(selectedLotId);
        setView("usage");
      }}
    />
    );
  }
    return (
    <div className="media-prep-scope">
    <MediaPrepHeaderSection />
    <MediaPrepPageHeader pageHeader={t('mediaPowder.title')} pageDescription={t('pageDescription.mediaPowderInventory.description')} />

    <div className="media-prep-scope px-8 pb-1 pt-3 max-w-full animate-in fade-in duration-500 min-h-full">
      <div className="flex items-start justify-between mb-2">
        <div className="space-y-1">
          {/* <h2 className='headText1 m-0 d-flex align-items-center' style={{ fontSize: '18px', height: '100%' }}>{t('mediaPowder.title')}</h2> */}
          {/* <p className="text-slate-400 text-[12.5px] font-medium">{t('mediaPowder.description')}</p> */}
        </div>
        <div className="flex items-center gap-4 mt-1 shrink-0">
          {permission?.includes("reconcile_stock") && (
          <button 
            onClick={() => navigate("/reconcileMediaPowder")}
            className="bg-white text-slate-700 border border-slate-200 flex items-center gap-2 px-6 py-1 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm focus:outline-none focus:ring-0 active:outline-none"
          >
            <span className="material-symbols-outlined text-xl">{t('mediaPowder.icons.rebase_edit')}</span> {t('mediaPowder.actions.reconcileStock')}
          </button>
          )}

          {permission?.includes("add_media_powder_inventory") && (
            <button 
              onClick={() => setShowLogModal(true)}
              className="bg-[#FF7043] text-white flex items-center gap-1 px-4 py-2 rounded-lg shadow-[0_8px_20px_rgba(255,112,67,0.2)] font-semibold text-xs uppercase tracking-wide hover:brightness-110 active:scale-95 transition-all border-none"
            >
              <span className="material-symbols-outlined">{t('mediaPowder.icons.add_box')}</span> {t('mediaPowder.actions.logShipment')}
            </button>
          )}

        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-3.5">
        {[
          { label: t('mediaPowder.stats.available'), val: stats.available.toLocaleString(), color: '#10B981', icon: 'check_circle', trend: t('mediaPowder.stats.qcApproved'), status: t('mediaPowder.stats.Released') },
          { label: t('mediaPowder.stats.quarantine'), val: stats.quarantine, color: '#8B5CF6', icon: 'hourglass_empty', trend: t('mediaPowder.stats.pendingQa'), status: t('mediaPowder.stats.Quarantine') },
          { label: t('mediaPowder.stats.rejected'), val: stats.blocked, color: '#EF4444', icon: 'cancel', trend: t('mediaPowder.stats.doNotUse'), status: t('mediaPowder.stats.Rejected') },
          { label: t('mediaPowder.stats.expired'), val: stats.expired, color: '#F59E0B', icon: 'event_busy', trend: t('mediaPowder.stats.dispose'), status: t('mediaPowder.stats.Expired') },
        ].map((box, i) => (
          <StatusCard
            key={i}
            label={box.label}
            value={box.val}
            color={box.color}
            icon={box.icon}
            trend={box.trend}
            status={box.status}
            selectedStatus={statusFilter}
            onClick={(status) =>
              setStatusFilter(statusFilter === status ? "" : status)
            }
          />
        ))}  
      </div>

      <FilterBar
        batchCodes={batchCodes}
        statuses={[t('mediaPowder.stats.Released'), t('mediaPowder.stats.Rejected'), t('mediaPowder.stats.Quarantine'), t('mediaPowder.stats.Expired')]}

        mediaTypes={plateTypes}
        selectedBatchCode={lotFilter}
        selectedStatus={statusFilter}
        selectedMediaType={mediaTypeFilter}
        fromDate={fromDate}
        toDate={toDate}
        searchTerm={searchInput}

        onBatchCodeChange={(val) => {
          setLotFilter(val);
          setCurrentPage(1);
        }}

        onStatusChange={(val) => {
          setStatusFilter(val);
          setCurrentPage(1);
        }}

        onMediaTypeChange={(val) => {
          setMediaTypeFilter(val);
          setCurrentPage(1);
        }}

        onFromDateChange={(val) => {
          setFromDate(val);
          setCurrentPage(1);
        }}

        onToDateChange={(val) => {
          setToDate(val);
          setCurrentPage(1);
        }}

        onSearchChange={(val) => {
          setSearchInput(val);

          if (!val) {
            setSearchTerm("");
            setCurrentPage(1);
          }
        }}
        onSearchClick={() => {
          setSearchTerm(searchInput);
          setCurrentPage(1);
        }}

        onClearFilters={handleClearFilters}
      />              
       <MediaPreparationTable
          columns={getColumns(t)}
          data={inventoryItems}
          loading={loading}
          currentPage={currentPage}
          pageSize={pageSize}
          onSortClick={handleSort}
          renderAction={(row) => (
            <button
              onClick={() => {
                setSelectedLotId(row.id);
                setSelectedRow(row);
                setView("lot");
              }}
              className="flex items-center justify-center gap-1 w-[90px] h-[26px] bg-[rgb(54,64,93)] text-white rounded-lg font-black text-[8px] uppercase tracking-widest hover:brightness-110 active:scale-95 transition-all shadow-sm border-none outline-none focus:outline-none"
            >
              <span className="material-symbols-outlined text-base">{t('mediaPowder.icons.visibility')}</span>
              {t('mediaPowder.actions.view')}
            </button>
          )}
        />

        <CustomToast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={2500}
          message={toastMessage}
          toastType={toastType}
        />
        
        <LogMediaPowderModal
          show={showLogModal}
          onClose={() => setShowLogModal(false)}
          onSuccess={() => {
            setShowLogModal(false);
            getMediaPowderList();
            getMediaPowderStats();
            getBatchCodes();
          }}
          title={t('mediaPowder.form.title')}
          FormComponent={PowderLotCreation}
          formProps={{ showToastMessage }}
        />
        </div>
        <Pagination
            page={currentPage}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
        />
      </div>
  );
};

export default MediaPowderInventory;
